import { metricNames } from '@linearb/linjs-common';
import get from 'lodash/get';
import {
  convertMetricsNameToV3,
  getTeamMetricsV3
} from 'common/services/metrics.service';
import { isTeamLicense } from 'common/utils/utils';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import useMetricsCalculationConfig from '../../../hooks/useMetricsCalculationConfig';
import useBenchmarkOrgSizeConfig from '../../../hooks/useBenchmarkOrgSizeConfig';
import { VIEW_PATH_KEYS } from 'common/constants/constants';
import {
  DASHBOARD_DEBOUNCE_PRIORITY,
  DEFAULT_REQUESTED_TEAM_METRICS
} from '../dashboard.consts';
import { createTrendDataV3 } from '../dashboard.service';
import { useDebounceCallback } from '@react-hook/debounce';
import { getIsGitScopeByKey } from 'components/TeamGitScopeBanner/utils';

export function useTrendData(props, setNoReleasesInIterations, gitScopeToggle) {
  const [workBreakdownTrendData, setWorkBreakdownTrendData] = useState({});
  const [cycleTimeTrendData, setCycleTimeTrendData] = useState({});
  const [reviewsTrendData, setReviewsTrendData] = useState({});
  const [deploymentFreqData, setDeploymentFreqTrendData] = useState({});
  const [isLodingCycleTimeData, setIsLoadingCycleTimeData] = useState(false);

  const {
    current5Iterations,
    isFinishedOnBoarding,
    isVPMode,
    uiState,
    isProcessingRepos,
    setHasNoData
  } = props;

  const { selectedTeamId } = uiState;
  const currTeamIdRef = useRef(null);
  const currGitScopeToggle = useRef(gitScopeToggle);
  const shouldFetchTrendData = current5Iterations?.[0]?.id ?? null;
  const isGroup = useSelector(isSelectedTeamParentSelector);
  const metricsCalculationConfig = useMetricsCalculationConfig();
  const benchmarkOrgSizeConfig = useBenchmarkOrgSizeConfig();

  const loadTrendData = async gitScopeToggle => {
    // Function called from useDebounceCallback. while debounce waits and changing team to All Teams - need to exit function
    // (if All Teams - this function shouldn't be called)
    if (!selectedTeamId) {
      return;
    }
    const time_ranges = current5Iterations.map(
      ({ start_date: after, end_date: before }) => ({ before, after })
    );
    const { metricsCalculationStrategy } = metricsCalculationConfig;
    const { benchmarkOrgSize } = benchmarkOrgSizeConfig;

    const v3Metrics = DEFAULT_REQUESTED_TEAM_METRICS;

    const joinedData = await getTeamMetricsV3({
      time_ranges,
      requested_metrics: convertMetricsNameToV3(
        v3Metrics,
        metricsCalculationStrategy
      ),
      teamId: selectedTeamId,
      ...(gitScopeToggle !== null
        ? {
            team_git_scope_disabled: !getIsGitScopeByKey(
              gitScopeToggle,
              VIEW_PATH_KEYS.DASHBOARD
            )
          }
        : {})
    });

    if (isTeamLicense) {
      const hasData = joinedData.some(item =>
        item.data?.some(data =>
          Object.values(data).some(dataItem => dataItem !== null)
        )
      );
      setHasNoData(!hasData);
    }

    setNoReleasesInIterations(
      get(joinedData, '[0].data', []).every(
        val => get(val, metricNames.RELEASES_COUNT, 0) === 0
      )
    );

    const {
      xData,
      yWorkBreakDownData,
      yCycleTimeData,
      yReviewDepthData,
      yReleasesData
    } = createTrendDataV3(
      joinedData,
      current5Iterations,
      metricsCalculationConfig,
      benchmarkOrgSize
    );

    setWorkBreakdownTrendData({ x: xData, y: yWorkBreakDownData });
    setCycleTimeTrendData({ x: xData, y: yCycleTimeData });
    setReviewsTrendData({ x: xData, y: yReviewDepthData });
    setDeploymentFreqTrendData({ x: xData, y: yReleasesData });
    setIsLoadingCycleTimeData(false);
  };

  const debouncedLoadTrendData = useDebounceCallback(gitScopeToggle => {
    loadTrendData(gitScopeToggle);
  }, DASHBOARD_DEBOUNCE_PRIORITY.LOW);

  useEffect(() => {
    if (
      (currTeamIdRef.current !== selectedTeamId ||
        currGitScopeToggle !== gitScopeToggle) &&
      shouldFetchTrendData &&
      isFinishedOnBoarding &&
      !isVPMode &&
      !isGroup
    ) {
      setIsLoadingCycleTimeData(true);
      debouncedLoadTrendData(gitScopeToggle);
    }
  }, [
    shouldFetchTrendData,
    selectedTeamId,
    isFinishedOnBoarding,
    isProcessingRepos,
    gitScopeToggle
  ]);

  useEffect(() => {
    currTeamIdRef.current = selectedTeamId;
  }, [selectedTeamId]);

  useEffect(() => {
    currGitScopeToggle.current = gitScopeToggle;
  }, [gitScopeToggle]);

  return {
    workBreakdownTrendData,
    setWorkBreakdownTrendData,
    cycleTimeTrendData,
    setCycleTimeTrendData,
    reviewsTrendData,
    setReviewsTrendData,
    deploymentFreqData,
    setDeploymentFreqTrendData,
    isLodingCycleTimeData
  };
}
